import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { InternalAuditReportByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditReportByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC internal audit report data to match the GraphQL query structure
 */
const mapTrpcInternalAuditReportToGraphQL = (
  trpcData: InternalAuditReportByIdResponseRow[]
): GetInternalAuditReportByIdQuery => {
  return {
    internal_audit_report: trpcData,
  };
};

export const useGetInternalAuditReportById = (
  reportId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditReport.internalAuditReportById.queryOptions({
      reportId,
    }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcInternalAuditReportToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
